
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Poll } from "@/types/poll";
import PollResultsChart from "@/components/polls/PollResultsChart";
import PollDemographicAnalysis from "@/components/polls/PollDemographicAnalysis";
import ThreadedComments from "@/components/social/ThreadedComments";
import { SocialMetrics } from "@/types/social";
import { useCommentCount } from "@/contexts/comments/CommentCountContext";
import { useVoteFiltering } from "@/hooks/polls/useVoteFiltering";
import { reconcilePollVotes } from "@/utils/polls/pollReconciliation";
import { useEffect } from "react";
import { useIsMobile } from "@/hooks/use-mobile";

interface PollContentTabsProps {
  activeTab: string;
  setActiveTab: (tab: string) => void;
  poll: Poll;
  userVoteOptionId: number | null;
  votesDistribution: any[] | null;
  demographicData: any;
  isDemographicsLoading: boolean;
  socialMetrics?: SocialMetrics;
}

const PollContentTabs = ({
  activeTab,
  setActiveTab,
  poll,
  userVoteOptionId,
  votesDistribution,
  demographicData,
  isDemographicsLoading,
  socialMetrics
}: PollContentTabsProps) => {
  const isMobile = useIsMobile();
  
  // Get vote statistics using useVoteFiltering hook
  const { voteStats } = useVoteFiltering(poll);
  
  // Get comment count from context when available
  let commentsCount = socialMetrics?.commentsCount || 0;
  
  try {
    const { commentCount } = useCommentCount();
    commentsCount = commentCount;
  } catch (error) {
    // Context not available, use props
    console.log("Comment count context not available in PollContentTabs");
  }
  
  // Ensure poll votes are reconciled when component mounts
  useEffect(() => {
    if (poll?.id) {
      reconcilePollVotes(poll.id).catch(err => 
        console.error("Failed to reconcile poll votes:", err)
      );
    }
  }, [poll?.id]);

  // Define available tabs
  const availableTabs = [
    { value: "results", label: "Results" },
    { value: "comments", label: `All Comments ${commentsCount ? `(${commentsCount})` : ''}` },
    ...(voteStats.totalVotes > 0 ? [{ value: "demographics", label: "Demographics" }] : [])
  ];

  const handleTabChange = (value: string) => {
    setActiveTab(value);
    window.history.replaceState(null, '', `${window.location.pathname}#${value}`);
  };

  return (
    <Card id="comments" className="w-full overflow-hidden">
      <CardHeader className="pb-4 overflow-hidden">
        {isMobile ? (
          // Mobile: Use dropdown selector
          <div className="w-full">
            <Select value={activeTab} onValueChange={handleTabChange}>
              <SelectTrigger className="w-full">
                <SelectValue>
                  {availableTabs.find(tab => tab.value === activeTab)?.label || "Select section"}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {availableTabs.map((tab) => (
                  <SelectItem key={tab.value} value={tab.value}>
                    {tab.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        ) : (
          // Desktop/Tablet: Use tabs as before
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full overflow-x-auto scrollbar-hide">
              <TabsTrigger 
                value="results" 
                onClick={() => window.history.replaceState(null, '', `${window.location.pathname}#results`)}
                className="whitespace-nowrap flex-shrink-0"
              >
                Results
              </TabsTrigger>
              <TabsTrigger 
                value="comments" 
                onClick={() => window.history.replaceState(null, '', `${window.location.pathname}#comments`)}
                className="whitespace-nowrap flex-shrink-0"
              >
                All Comments {commentsCount ? `(${commentsCount})` : ''}
              </TabsTrigger>
              {voteStats.totalVotes > 0 && (
                <TabsTrigger 
                  value="demographics" 
                  onClick={() => window.history.replaceState(null, '', `${window.location.pathname}#demographics`)}
                  className="whitespace-nowrap flex-shrink-0"
                >
                  Demographics
                </TabsTrigger>
              )}
            </TabsList>
          </Tabs>
        )}
      </CardHeader>
      <CardContent className="w-full overflow-x-hidden">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsContent value="results" className="w-full overflow-x-hidden">
            <div className="mb-6 w-full overflow-x-hidden">
              <p className="text-gray-500 dark:text-gray-400 mb-4">
                Total responses: {voteStats.totalVotes}
              </p>
              <div className="w-full overflow-x-hidden">
                <PollResultsChart 
                  votesDistribution={votesDistribution || []}
                  userVoteOptionId={userVoteOptionId}
                />
              </div>
            </div>
          </TabsContent>

          <TabsContent value="comments" className="w-full overflow-x-hidden">
            <ThreadedComments 
              contentId={poll.id} 
              contentType="poll" 
              initialPageSize={10} 
            />
          </TabsContent>

          <TabsContent value="demographics" className="w-full overflow-x-hidden">
            {voteStats.totalVotes > 0 ? (
              <div className="w-full overflow-x-hidden">
                <PollDemographicAnalysis 
                  demographicData={demographicData} 
                  isLoading={isDemographicsLoading}
                />
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-gray-500">No demographic data available yet.</p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default PollContentTabs;
